import { useEffect, useState } from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { useLocation } from "react-router";

import { useRootStore } from "store";
import {
  filterYearEndFolders,
  isReportsFolder,
  NODE_TYPE_FOLDER,
  NODE_TYPE_ROOT
} from "views/documents/state/documentArchiveOptions";
import InfoIconTooltip from "components/tooltip/InfoIconTooltip";
import { Loader } from "components/loader/Loader";
import { SideMenu } from "components/menu/SideMenu/SideMenu";
import { DocumentArchiveFolderTree } from "../components/folderTree/DocumentArchiveFolderTree";
import { FileUploadList } from "components/fileInput/FileUploadList";
import { ArchiveItem } from "models/ArchiveItem";
import { DocumentArchiveFileTableWithOptions } from "../components/fileTable/DocumentArchiveFileTableInternalWithOptions";
import { DocumentArchiveFileTableExternal } from "../components/fileTable/DocumentArchiveFileTableExternal";
import useSetErrorOnUpload from "hooks/use-set-error-on-upload";
import FileUploadErrorModal from "components/shared/fileUploadErrorModal/FileUploadErrorModal";
import { useRequiredCustomerId } from "hooks/use-customer-id";
import { usePermission } from "hooks/use-permissions";

const getQueryParam = (search: string, param: string) =>
  new URLSearchParams(search).get(param);

export default function DocumentArchive() {
  const location = useLocation();
  const customerId = useRequiredCustomerId();

  const { archive } = useRootStore((state) => state);
  const isInternalUser = usePermission("isInternal");

  const {
    downloadArchiveFile,
    fetchArchiveFolders,
    getArchiveFolderFiles,
    resetArchive,
    sendFileToArchive,
    setSelectedArchiveNodeId,
    toggleArchiveTreeNodeId,
    archiveRoot
  } = archive;

  const { failed, setFailed, failMessage, setFailedWrapper } =
    useSetErrorOnUpload();

  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    resetArchive();
  }, [customerId, resetArchive]);

  useEffect(() => {
    fetchArchiveFolders(customerId, true);

    const folderId = getQueryParam(location.search, "folderId");
    if (folderId !== null) {
      setSelectedArchiveNodeId(folderId);
      getArchiveFolderFiles(customerId, folderId);
    }
  }, [
    customerId,
    location.search,
    fetchArchiveFolders,
    getArchiveFolderFiles,
    setSelectedArchiveNodeId
  ]);

  const handleTreeNodeClicked = (node: ArchiveItem, hasChildren: boolean) => {
    if (!hasChildren || node.id.startsWith('reports')) {
      getArchiveFolderFiles(customerId, node.id);
    }

    if (node.type === NODE_TYPE_FOLDER || node.type === NODE_TYPE_ROOT) {
      toggleArchiveTreeNodeId(node.id);
      setSelectedArchiveNodeId(node.id);
      setCurrentTab(0);
    }
  };

  const handleUploadClick = async (files: File[]) => {
    const selectedNode = archiveRoot.find(
      (n) => n.id === archive.selectedFolderId
    );

    if (selectedNode === undefined) {
      return;
    }

    try {
      const response = await sendFileToArchive(
        files.map((f) => ({
          meta: undefined,
          file: f
        })),
        customerId,
        selectedNode.path,
        setFailedWrapper
      );

      if (!response) {
        return;
      }
      setCurrentTab(0);
    } catch {
      setCurrentTab(1);
    }
  };

  const renderFileListEmployee = (selectedNode: ArchiveItem) => {
    const isDisabled = selectedNode === undefined;
    const isRootFolder = isDisabled || selectedNode.id === NODE_TYPE_ROOT;
    const isReadonlyFolder = isRootFolder || !selectedNode.writable || isReportsFolder(selectedNode.id);

    if (archive.isLoadingFolder) {
      return <Loader />;
    }

    if (!selectedNode.writable) {
      return <p>Välj en undermapp i vänstermenyn.</p>;
    }

    return (
      <div>
        <Tabs selectedIndex={currentTab} onSelect={setCurrentTab}>
          <TabList>
            <Tab>Filer</Tab>
            {!isReadonlyFolder && <Tab>Ladda upp filer</Tab>}
          </TabList>

          <TabPanel>
            {archive.isUploading ? (
              <div className={"place-items-center"}>
                <Loader className={"mr-3"} /> Laddar upp filer...
              </div>
            ) : (
              <DocumentArchiveFileTableWithOptions
                folder={selectedNode}
                files={archive.selectedFolderFiles}
              />
            )}
          </TabPanel>

          <TabPanel>
            {archive.isUploading ? (
              <div className={"place-items-center"}>
                <Loader className={"mr-3"} /> Laddar upp filer...
              </div>
            ) : (
              <FileUploadList
                isUploading={archive.isUploading}
                onUploadClick={handleUploadClick}
              />
            )}
          </TabPanel>
        </Tabs>
      </div>
    );
  };

  const renderFileListCustomer = (selectedNode: ArchiveItem) => {
    if (archive.isLoadingFolder || archive.isLoadingArchiveRoot) {
      return <Loader />;
    }

    if (archive.selectedFolderFiles.length === 0) {
      return <p>Här var det tomt!</p>;
    }

    if (!selectedNode.writable) {
      return <p>Välj en undermapp i vänstermenyn.</p>;
    }

    return (
      <DocumentArchiveFileTableExternal
        onDownloadFile={(file) => downloadArchiveFile(customerId, file)}
        files={archive.selectedFolderFiles}
      />
    );
  };

  const renderFileList = () => {
    const selectedNode = archiveRoot.find(
      (n) => n.id === archive.selectedFolderId
    );

    if (!selectedNode || selectedNode.id === NODE_TYPE_ROOT) {
      return <>Välj en mapp till vänster.</>;
    }

    return (
      <>
        <h1 className="mt-0 mb-4">{selectedNode.name}</h1>
        <hr className={"header-border"} />

        {isInternalUser && renderFileListEmployee(selectedNode)}
        {!isInternalUser && renderFileListCustomer(selectedNode)}
      </>
    );
  };

  return (
    <>
      {failed && (
        <FileUploadErrorModal failMessage={failMessage} setFailed={setFailed} />
      )}
      <div
        data-testid="document-archive"
        className="flex-column-md-down d-flex flex-wrap align-items-start"
      >
        <SideMenu
          title={
            <span className="d-flex align-items-center justify-content-between">
              Dokumentarkivet
              {isInternalUser && (
                <InfoIconTooltip className="ml-3">
                  <p className="mt-0">
                    I dokumentarkivet ska du lagra kundens material. Interna
                    dokument och tjänsteanteckningar lagras på kundteamet i
                    Teams. I Dokumentarkivet kan du ladda upp filer ifrån din
                    dator eller flytta filer ifrån fliken Inleveranser. Från
                    Inleveranser kan du flytta filer ifrån Nya Inleveranser och
                    Hanterade. Kunden kan inte göra några korrigeringar i
                    Dokumentarkivet.
                  </p>
                  <h5 className="m-0 mb-0">Ladda upp filer</h5>
                  <p>
                    Markera undermappen i vänsterspalten som du vill ladda upp
                    en fil till. Klicka på fliken <b>Ladda upp filer</b> i
                    högerspalten, markera filen och klicka på ”öppna” eller dra
                    filer till det gråa fältet. Avsluta med att klicka på
                    knappen <b>Ladda upp</b>. Du kan endast ladda upp 15 filer
                    åt gången.
                  </p>
                </InfoIconTooltip>
              )}
            </span>
          }
        >
          <div className="d-flex">
            <div className="d-flex flex-direction-column align-items-start w-100">
              <DocumentArchiveFolderTree
                isLoading={archive.isLoadingArchiveRoot}
                onFolderClicked={handleTreeNodeClicked}
                selectedFolderId={archive.selectedFolderId}
                folders={archiveRoot.filter((item) =>
                  filterYearEndFolders(item, archiveRoot)
                )}
              />
            </div>
          </div>
        </SideMenu>

        <div className="box flex-1-1 overflow-y-auto w-100">
          {renderFileList()}
        </div>
      </div>
    </>
  );
}
